import db from "@/db.server";
import { authenticate } from "@/shopify.server";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  isRouteErrorResponse,
  useRouteError,
} from "@remix-run/react";
import * as React from "react";
import { PageReloadErrorMessage } from "./components/common/PageReloadErrorMessage";
import { ConfirmationModalProvider } from "./components/discount/general/confirmation-modal";
import "./global.css";
import {
  configureClientLogger,
  setNavigationId,
} from "./logging/console.client";
import { PHProvider } from "./PostHogProvider";
import { useShopDataStore } from "./store/shopDataStore";
import { setClarityTag, setClarityUserId } from "./utils/clarity";
import {
  CUSTOM_PLAN_DURATIONS,
  isActiveCustomPricingStatus,
  resolveCustomPricingDefinition,
} from "./utils/custom-pricing";
import { EPlanId, FREE_TRIAL_DAYS } from "./utils/pricing";
import { syncSupportChat } from "./utils/supportChat.client";
import { shouldRevalidateDiscountEditor } from "./utils/discountEditorRevalidation";
import { getVariantConfig } from "./variants/index.server";
import type { IVariantIcons } from "./variants/types";

/** Builds the merchant-facing "Tier Duration $price" string (e.g. "Starter Monthly $39.99") for shops actively on CUSTOM pricing. */
async function getCustomPlanDetails(sessionId: string): Promise<string | null> {
  const customPricing = await db.shopCustomPricing.findFirst({
    where: { shopData: { sessionId } },
    select: {
      status: true,
      planKey: true,
      planDuration: true,
      basePriceUSD: true,
    },
  });

  if (!customPricing || !isActiveCustomPricingStatus(customPricing.status)) {
    return null;
  }

  const definition = resolveCustomPricingDefinition(
    customPricing.planKey,
    customPricing.planDuration,
  );
  const durationLabel =
    customPricing.planDuration === CUSTOM_PLAN_DURATIONS.ANNUAL
      ? "Yearly"
      : "Monthly";

  return `${definition?.displayName ?? customPricing.planKey} ${durationLabel} $${customPricing.basePriceUSD.toFixed(2)}`;
}

const AUTH_PATH_PREFIX = "/auth";

/**
 * Document routes that must render WITHOUT a Shopify admin session.
 *
 * The loader below authenticates every other path, which is right for the embedded app but fatal
 * for a page Shopify never frames: `/oauth/authorize` is opened by an MCP client in a plain
 * browser tab, so there is no session token to exchange and `authenticate.admin` throws — the
 * merchant just sees the generic "Refresh the page" boundary.
 *
 * Only HTML pages need listing. Resource routes (`/mcp`, `/oauth/token`, `/.well-known/*`) have no
 * default export, so Remix never runs the root loader for them.
 */
const PUBLIC_PATH_PREFIXES = ["/oauth/"];

/**
 * Favicon links for the app this deployment is running as.
 *
 * These cannot be a Remix `links` export: that function receives no arguments, so it cannot see
 * which variant the loader resolved, and every app is served from the same build. Emitting the
 * tags as head JSX is what keeps AIOD's and Free Gift's icons apart on their own domains.
 *
 * `icons: null` (an app with no artwork yet) renders nothing, leaving crawlers on a generic
 * globe rather than another app's badge. See IVariantPublicSite.
 */
function AppIconLinks({ icons }: { icons: IVariantIcons | null }) {
  if (!icons) return null;

  return (
    <>
      <link rel="icon" type="image/x-icon" href={icons.faviconIco} />
      <link rel="icon" type="image/png" sizes="192x192" href={icons.iconPng} />
      <link rel="apple-touch-icon" href={icons.iconPng} />
    </>
  );
}

// Crisp only stores a browsed page when the document has a title. Keep a
// global fallback so every Remix route can be recorded, even when the route
// does not provide more specific metadata.
export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.appVariantId ?? "App" },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const isAuthRoute =
    url.pathname.startsWith(AUTH_PATH_PREFIX) ||
    PUBLIC_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix));

  const { getNewRelicBrowserTimingScriptInnerHtml } =
    await import("./utils/newrelic-browser-timing.server");
  const nrBrowserTimingScriptInnerHtml =
    getNewRelicBrowserTimingScriptInnerHtml();

  // The id of the request that served this document. Browser records carry it as
  // `pageRequestId`, which is what joins "the merchant saw a broken page" to the server records
  // for the page load that produced it.
  const { getRequestContext } = await import("./logging/context.server");
  const pageRequestId = getRequestContext()?.requestId ?? null;

  const variantConfig = getVariantConfig();

  const isIndexRoute = url.pathname === "/";

  if (isAuthRoute || isIndexRoute) {
    // Auth routes and root index have no session - return minimal data
    return {
      isAuthRoute: true,
      myShopifyDomain: null,
      isDevEnv: process.env.NODE_ENV === "development",
      nrBrowserTimingScriptInnerHtml,
      pageRequestId,
      customPlanDisplayName: null,
      appVariantId: variantConfig.id,
      icons: variantConfig.publicSite.icons,
      ENV: {
        POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
        POSTHOG_HOST: process.env.POSTHOG_HOST,
      },
    };
  }

  const { session } = await authenticate.admin(request);
  const customPlanDisplayName = await getCustomPlanDetails(session.id);

  return {
    isAuthRoute: false,
    myShopifyDomain: session.shop,
    isDevEnv: process.env.NODE_ENV === "development",
    nrBrowserTimingScriptInnerHtml,
    pageRequestId,
    customPlanDisplayName,
    appVariantId: variantConfig.id,
    icons: variantConfig.publicSite.icons,
    ENV: {
      POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
      POSTHOG_HOST: process.env.POSTHOG_HOST,
    },
  };
};

export const shouldRevalidate = shouldRevalidateDiscountEditor;

declare global {
  interface Window {
    clarity: (action: string, ...args: any[]) => void;
    ENV?: {
      POSTHOG_API_KEY?: string;
      POSTHOG_HOST?: string;
    };
  }
}

function reportRouteError(error: unknown, pathname: string) {
  // A 4xx route error is the router saying there is nothing at this URL, not a page that broke.
  // Most of them are vulnerability scanners walking the internet, and reporting those as render
  // failures put bot traffic in the error inbox and the alerting path.
  if (isRouteErrorResponse(error) && error.status < 500) {
    console.warn(
      "app.route.unmatched",
      { path: pathname, status: error.status },
      `No route at ${pathname} (${error.status})`,
    );
  } else {
    console.error(
      "app.page.load.failed",
      error,
      { path: pathname },
      `Page failed to render: ${pathname}`,
    );
  }
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  const { pathname } = useLocation();
  // A route-level render failure is the merchant seeing a broken page. It reaches New Relic
  // Browser through the client console capture, with the path, so it can be correlated with the
  // records for the same navigation.
  //
  // On the server it is logged here, during render: an error page rendered by the server is a bare
  // `<div>` with no scripts, so it never hydrates and no effect would ever run for it.
  // In the browser it is logged from an effect instead. When hydration fails, React renders this
  // boundary in an attempt it then discards and re-renders the page on the client — the merchant
  // gets a working page. Logged during render, every one of those recoveries was reported as a
  // failed page (`app.render.recovered` in entry.client.tsx records them for what they are).
  // Effects run only for what React commits, so this counts the error screens merchants saw.
  if (typeof window === "undefined") reportRouteError(error, pathname);
  React.useEffect(() => {
    reportRouteError(error, pathname);
  }, [error, pathname]);
  return PageReloadErrorMessage();
};

export default function App() {
  const {
    isAuthRoute,
    myShopifyDomain,
    isDevEnv,
    ENV,
    nrBrowserTimingScriptInnerHtml,
    pageRequestId,
    customPlanDisplayName,
    appVariantId,
    icons,
  } = useLoaderData<typeof loader>();
  // React Router mints a fresh key per history entry, which is exactly one per navigation.
  const { key: navigationKey } = useLocation();

  // `entry.client.tsx` installs the capture before hydration; this only fills in who and which
  // page load, which are not known until the loader has run.
  if (typeof window !== "undefined") {
    configureClientLogger(myShopifyDomain, pageRequestId);
    // Changes once per navigation, so every data request of one page load shares it.
    setNavigationId(navigationKey);
  }

  // Auth routes get minimal layout - no tracking, no app providers
  if (isAuthRoute) {
    return (
      <html>
        <head>
          <meta charSet="utf-8" />
          {nrBrowserTimingScriptInnerHtml ? (
            <script
              suppressHydrationWarning
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: nrBrowserTimingScriptInnerHtml,
              }}
            />
          ) : null}
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <Meta />
          <Links />
          <AppIconLinks icons={icons} />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(ENV)}`,
            }}
          />
        </head>
        <body>
          <Outlet />
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <AuthenticatedApp
      myShopifyDomain={myShopifyDomain}
      isDevEnv={isDevEnv}
      ENV={ENV}
      nrBrowserTimingScriptInnerHtml={nrBrowserTimingScriptInnerHtml}
      customPlanDisplayName={customPlanDisplayName}
      appVariantId={appVariantId}
      icons={icons}
    />
  );
}

function AuthenticatedApp({
  myShopifyDomain,
  isDevEnv,
  ENV,
  nrBrowserTimingScriptInnerHtml,
  customPlanDisplayName,
  appVariantId,
  icons,
}: {
  myShopifyDomain: string | null;
  isDevEnv: boolean;
  ENV: { POSTHOG_API_KEY?: string; POSTHOG_HOST?: string };
  nrBrowserTimingScriptInnerHtml?: string;
  customPlanDisplayName: string | null;
  appVariantId: string;
  icons: IVariantIcons | null;
}) {
  const {
    planName,
    sessionUsers,
    currSessionUserId,
    isEarlyUser,
    aiod_plan,
    ownerEmail,
    freeTrialStartedAt,
    hasLoaded: hasLoadedClientContext,
  } = useShopDataStore();
  const aiodPlanDisplay =
    aiod_plan === EPlanId.CUSTOM && customPlanDisplayName
      ? customPlanDisplayName
      : aiod_plan || "";
  const currentUser = sessionUsers.find(
    (user) => user.id === currSessionUserId,
  );

  if (!isDevEnv && myShopifyDomain) {
    setClarityUserId(myShopifyDomain);
  }

  React.useEffect(() => {
    if (!isDevEnv && myShopifyDomain) {
      if (myShopifyDomain) setClarityTag("myShopifyDomain", myShopifyDomain);
      if (aiodPlanDisplay) setClarityTag("aiodPlan", aiodPlanDisplay);
      if (typeof isEarlyUser === "boolean")
        setClarityTag("isEarlyUser", isEarlyUser ? "true" : "false");
      if (planName) setClarityTag("shopifyPlan", planName);
      if (currentUser?.name) setClarityTag("fullName", currentUser.name);
      if (currentUser?.email)
        setClarityTag("sessionUserEmail", currentUser.email);
      if (typeof currentUser?.isOwner === "boolean")
        setClarityTag("isOwner", currentUser.isOwner ? "true" : "false");
      if (typeof currentUser?.isCollaborator === "boolean")
        setClarityTag(
          "isCollaborator",
          currentUser.isCollaborator ? "true" : "false",
        );
    }
  }, [
    aiod_plan,
    aiodPlanDisplay,
    isEarlyUser,
    planName,
    currentUser,
    isDevEnv,
    myShopifyDomain,
  ]);

  React.useEffect(() => {
    if (!hasLoadedClientContext) return;

    let freeTrialStatus = "NA";
    if (freeTrialStartedAt) {
      const trialPeriodEndsAt =
        new Date(freeTrialStartedAt).getTime() +
        FREE_TRIAL_DAYS * 24 * 60 * 60 * 1000;
      const freeTrialRemainingDays = Math.ceil(
        (trialPeriodEndsAt - Date.now()) / (1000 * 60 * 60 * 24),
      );
      freeTrialStatus =
        freeTrialRemainingDays > 0
          ? `${freeTrialRemainingDays} days remaining`
          : "Expired";
    }
    syncSupportChat({
      sessionToken: currentUser?.supportChatSessionToken,
      myShopifyDomain: myShopifyDomain || "",
      ownerEmail: ownerEmail || "",
      shopifyPlan: planName || "",
      fullName: currentUser?.name || "",
      sessionUserEmail: currentUser?.email || "",
      isOwner: currentUser?.isOwner || false,
      isCollaborator: currentUser?.isCollaborator || false,
      isEarlyUser: isEarlyUser || false,
      aiodPlan: aiodPlanDisplay,
      freeTrialStatus,
      appVariantId,
    });
  }, [
    aiod_plan,
    aiodPlanDisplay,
    appVariantId,
    currentUser,
    freeTrialStartedAt,
    hasLoadedClientContext,
    isEarlyUser,
    myShopifyDomain,
    ownerEmail,
    planName,
  ]);

  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        {nrBrowserTimingScriptInnerHtml ? (
          <script
            suppressHydrationWarning
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html: nrBrowserTimingScriptInnerHtml,
            }}
          />
        ) : null}
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="preconnect" href="https://cdn.shopify.com/" />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />
        <Meta />
        <Links />
        <AppIconLinks icons={icons} />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`,
          }}
        />
        {/* Microsoft Clarity Script - Only load in non-development environments */}
        {!isDevEnv && (
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "rv4mtg4hq5");`,
            }}
          />
        )}
        {/* Google Tag Manager Script - Only load in non-development environments */}
        {!isDevEnv && (
          <>
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=AW-986561044"
            ></script>
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', 'AW-986561044');
                `,
              }}
            />
          </>
        )}
      </head>
      <body>
        <PHProvider>
          <ConfirmationModalProvider>
            <Outlet />
            <ScrollRestoration />
            <Scripts />
          </ConfirmationModalProvider>
        </PHProvider>
      </body>
    </html>
  );
}
